/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";

// VerticalBarChart configurations
import configs2 from "examples/Charts/BarCharts/VerticalBarChart/configs/index2";
// Material Dashboard 2 React base styles
import colors from "assets/theme/base/colors";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function VerticalBarChart2({
  icon,
  title,
  description,
  height,
  chart,
  xAxisTitle,
  yAxisTitle,
  onClickBar,
  customOptions,
}) {
  const { t } = useTranslation();

  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        weight: 5,
        borderWidth: 0,
        borderRadius: 4,
        backgroundColor: "#3A6EA5",
        fill: false,
        maxBarThickness: 35,
      }))
    : [];

  // const { data, options } = configs(chart.labels || [], chartDatasets);

  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const element = elements[0];
      const datasetIndex = element.datasetIndex;
      const dataIndex = element.index;
      const clickedData = chart.datasets[datasetIndex].data[dataIndex];
      const label = chart.labels[dataIndex]; // Assuming the labels are meaningful, e.g., job titles

      // Trigger onClickBar callback to pass selected data to the parent
      if (onClickBar) {
        onClickBar(clickedData, label); // Passing both clickedData and label (job) to the parent callback
      }
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // xs & sm

  const { data, options } = configs2(
    chart.labels || [],
    chartDatasets,
    xAxisTitle,
    yAxisTitle || t("user.dashboards.result"),
    onClickBar,
    isMobile
  );

  const mergedOptions = {
    ...options,
    ...customOptions,
    onClick: handleChartClick,
  };

  const renderChart = (
    <MDBox pt={0} pr={2} pl={icon.component ? 1 : 2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon.color || "dark"}
              variant="gradient"
              coloredShadow={icon.color || "dark"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      {useMemo(
        () => (
          <MDBox height={height}>
            <Bar data={data} options={mergedOptions} redraw />
          </MDBox>
        ),
        [chart, height]
      )}
    </MDBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of VerticalBarChart
VerticalBarChart2.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
  yAxisTitle: "", // customOptions: PropTypes.object,
  customOptions: {}, // Default empty object
  onClickBar: null, // Default to no action on click
};

// Typechecking props for the VerticalBarChart
VerticalBarChart2.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  xAxisTitle: PropTypes.string, // New prop for x-axis title
  yAxisTitle: PropTypes.string, // New prop for y-axis title
  onClickBar: PropTypes.func, // New prop for onClick callback
  customOptions: PropTypes.object, // New prop for custom options
};

export default VerticalBarChart2;
